import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  Box, Typography, Chip, TextField, Button, CircularProgress,
  Divider, List, ListItem, ListItemText, Paper, Stack, IconButton
} from '@mui/material';
import dayjs from 'dayjs';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import useApiService from '../hooks/useApiService';
import { useAppSnackbar } from '../providers/SnackbarProvider';
import { apiUrls } from '../../constans/apiUrls';
import stringFormat from '../common/stringFormat';

const pretty = (s) =>
  s ? String(s).replaceAll('_', ' ').replace(/\b\w/g, m => m.toUpperCase()) : '-';

const isImagePath = (p = '') => /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(p);

// Backend returns "attachments/<aid>/<tid>/<file>"; static route is "/api/boost/static/<aid>/<tid>/<file>"
const toStaticPath = (raw) => {
  if (!raw) return '';
  const clean = String(raw).replace(/^\/+/, '');
  return `/api/boost/static/${clean.replace(/^attachments\//, '')}`;
};

// Human-readable messages for backend error codes
const ERROR_MESSAGES = {
  invalid_payload: 'Lütfen mesajınızı yazın.',
  not_found: 'Böyle bir ticket veya assignment yok.',
  server_error: 'Bilinmeyen bir hata oluştu.',
};

function SecureImage({ api, path, alt = 'attachment', maxWidth = 240, maxHeight = 200, sx }) {
  const [url, setUrl] = useState(null);
  const staticUrl = useMemo(() => toStaticPath(path), [path]);

  useEffect(() => {
    let alive = true;
    let objectUrl;
    (async () => {
      try {
        const blob = await api.executeGetBlob({ url: staticUrl });
        objectUrl = URL.createObjectURL(blob);
        if (alive) setUrl(objectUrl);
      } catch (e) {
        // Optionally show a small placeholder or error indicator
        console.error('SecureImage fetch error:', e);
      }
    })();
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [api, staticUrl]);

  if (!url) return <Box sx={{ width: maxWidth, height: maxHeight, bgcolor: '#eee', borderRadius: 1 }} />;

  return (
    <Box
      component="img"
      src={url}
      alt={alt}
      sx={{ mt: 0, maxWidth, maxHeight, borderRadius: 1, display: 'block', ...sx }}
    />
  );
}

export default function TicketDetailUI({
  detail, loading, error,
  sending, onSend, onGoAssignment, openType
}) {
  const { executeGetBlob, executePost } = useApiService();
  const { showSnackbar } = useAppSnackbar();
  const msgRef = useRef(null);
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);


  useEffect(() => {
    if (detail?.ticket?.id) {
      onTicketRead();
    }
  }, [detail?.ticket])

  const onTicketRead = async () => {
    await executePost({
      url: stringFormat(apiUrls.TicketRead, detail?.ticket?.id),
    });
  }
  // Auto-scroll
  useEffect(() => {
    document.getElementById('messageEnd')?.scrollIntoView({ behavior: 'smooth' });
  }, [detail?.messages]);

  if (loading) return <CircularProgress sx={{ m: 4 }} />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!detail) return null;

  const { ticket, messages } = detail;

  // Send and show user-friendly errors via Snackbar
  const handleSend = async () => {
    const text = msgRef.current?.value?.trim() || '';

    if (!text && !file) {
      showSnackbar(ERROR_MESSAGES.invalid_payload, 'warning');
      return;
    }

    const { ok, code } = await onSend({ text, file });

    if (ok) {
      if (msgRef.current) msgRef.current.value = '';
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } else {
      const msg = ERROR_MESSAGES[code] || ERROR_MESSAGES.server_error;
      showSnackbar(msg, 'error');
    }
  };

  // Secure download for non-image attachments
  const handleSecureDownload = async (rawPath) => {
    try {
      const url = toStaticPath(rawPath);
      const blob = await executeGetBlob({ url });
      const name = String(rawPath).split('/').pop() || 'download';
      const a = document.createElement('a');
      const obj = URL.createObjectURL(blob);
      a.href = obj; a.download = name; a.click();
      URL.revokeObjectURL(obj);
    } catch (e) {
      showSnackbar(ERROR_MESSAGES.server_error, 'error');
    }
  };

  const renderMessage = (m) => {
    const text = m.body ?? m.text ?? '';
    const imagePath = m.image_path ?? m.imagePath ?? null;

    if (imagePath) {
      if (isImagePath(imagePath)) {
        return (
          <Box>
            {!!text && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                {text}
              </Typography>
            )}
            <SecureImage api={{ executeGetBlob }} path={imagePath} alt="attachment" />
          </Box>
        );
      }
      const fileName = String(imagePath).split('/').pop();
      return (
        <Box>
          {!!text && (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
              {text}
            </Typography>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => handleSecureDownload(imagePath)}
          >
            İndir {fileName}
          </Button>
        </Box>
      );
    }

    return (
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {text}
      </Typography>
    );
  };

  // Optional note split
  let noteMain = ticket.user_note || '';
  let preferred = null;
  if (noteMain.includes('\nPreferred:')) {
    [noteMain, preferred] = noteMain.split('\nPreferred:').map((s) => s.trim());
  }

  const stateColor = {
    open: 'default', approved: 'success', rejected: 'error', closed: 'info',
  }[ticket.state] ?? 'default';

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      {/* USER NOTE */}
      {noteMain && (
        <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Kullanıcı Mesajı
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', fontWeight: 500 }}>
            {noteMain}
          </Typography>
          {preferred && (
            <Chip sx={{ mt: 1 }} label={`Preferred : ${preferred}`} size="small" color="info" />
          )}
        </Paper>
      )}

      {/* HEADER */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6">Ticket #{ticket.ticket_id ?? ticket.id}</Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mt: 1 }}>
          <Chip label={pretty(ticket.state)} color={stateColor} size="small" />
          {ticket.bucket && <Chip label={`Bucket: ${pretty(ticket.bucket)}`} size="small" />}
          {ticket.category_code && <Chip label={`Kategori: ${ticket.category_code}`} size="small" />}
          {ticket.rule_name && <Chip label={`Rule: ${ticket.rule_name}`} size="small" />}
          {ticket.assignment_id && (
            <Chip
              label={`Assignment #${ticket.assignment_id}`}
              size="small"
              variant="outlined"
            //onClick={() => onGoAssignment(ticket.assignment_id)}
            />
          )}
          <Typography variant="caption" sx={{ ml: 'auto' }}>
            {dayjs(ticket.created_at).format('YYYY-MM-DD HH:mm')}
          </Typography>
        </Stack>
      </Paper>

      {/* CHAT */}
      <List sx={{ maxHeight: 380, overflowY: 'auto', mb: 3 }}>
        {messages.map((m) => (
          <ListItem
            key={m.id}
            sx={{ justifyContent: m.direction === 'admin' ? 'flex-end' : 'flex-start' }}
          >
            <Box
              sx={{
                maxWidth: '70%',
                background: m.direction === 'admin' ? '#e3f2fd' : '#f5f5f5',
                p: 1.5,
                borderRadius: 2,
              }}
            >
              <ListItemText
                primary={renderMessage(m)}
                secondary={dayjs(m.created_at).format('YYYY-MM-DD HH:mm')}
              />
            </Box>
          </ListItem>
        ))}
        <div id="messageEnd" />
      </List>

      <Divider sx={{ mb: 2 }} />

      {/* REPLY AREA */}
      {openType === "Chat" &&
        <Stack spacing={1}>
          <TextField
            inputRef={msgRef}
            multiline
            minRows={2}
            placeholder="Cevap yaz..."
            fullWidth
          />

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <input
                type="file"
                ref={fileRef}
                accept="image/*,application/pdf"
                style={{ display: 'none' }}
                id="ticket-attach"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
              <label htmlFor="ticket-attach">
                <Button variant="outlined" component="span" startIcon={<UploadFileIcon />}>
                  Dosya Ekle
                </Button>
              </label>

              {file && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="caption" sx={{ maxWidth: 160 }} noWrap>
                    {file.name}
                  </Typography>
                  <IconButton
                    aria-label="remove"
                    size="small"
                    onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ''; }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            </Stack>

            <Button variant="contained" disabled={sending} onClick={handleSend}>
              {sending ? <CircularProgress size={20} /> : 'Gönder'}
            </Button>
          </Stack>
        </Stack>}
    </Box>
  );
}

